/*
 * AddressAutocomplete
 * -------------------
 * Live Google Places autocomplete for AU residential addresses, with a
 * graceful fallback to a hardcoded suggestion list when no API key is
 * configured (so local dev keeps working without billing on).
 *
 * Behaviour:
 *  - On focus / typing we call `AutocompleteService.getPlacePredictions`
 *    debounced by ~200ms, restricted to AU + `address` types so we get
 *    street-level results, not businesses or regions.
 *  - On selection we call `PlacesService.getDetails(placeId, [...])` and
 *    map the resulting `address_components` array into the
 *    `{ street, suburb, state, postcode, formatted }` shape every parent
 *    component already expects (current/previous address, employer address,
 *    property address). No consumer changes needed.
 *
 * The Google billable surface used:
 *  - Places API (legacy) - AutocompleteService.getPlacePredictions
 *  - Places API (legacy) - PlacesService.getDetails
 *  - Maps JavaScript API - loader
 *
 * To swap in the newer Place Autocomplete (web component) later, replace
 * the body of `fetchPredictions` / `pickSuggestion` - the public props
 * stay the same.
 */
import React from 'react';
import { loadGoogleMaps, isGoogleMapsConfigured } from './googleMapsLoader';

// Fallback list when the API key is missing. Used in dev or as a graceful
// degradation if the script fails to load.
const STUB_SUGGESTIONS = [
  {
    formatted: 'Suite 2, Level 2, 88 Pitt Street, Sydney NSW 2000',
    unit_number: 'Suite 2, Level 2',
    street_number: '88',
    street_name: 'Pitt Street',
    street: 'Suite 2, Level 2, 88 Pitt Street',
    suburb: 'Sydney',
    state: 'NSW',
    postcode: '2000',
  },
  {
    formatted: '45 George Street, Sydney NSW 2000',
    unit_number: '',
    street_number: '45',
    street_name: 'George Street',
    street: '45 George Street',
    suburb: 'Sydney',
    state: 'NSW',
    postcode: '2000',
  },
  {
    formatted: '12 Bondi Road, Bondi NSW 2026',
    unit_number: '',
    street_number: '12',
    street_name: 'Bondi Road',
    street: '12 Bondi Road',
    suburb: 'Bondi',
    state: 'NSW',
    postcode: '2026',
  },
  {
    formatted: '101 Collins Street, Melbourne VIC 3000',
    unit_number: '',
    street_number: '101',
    street_name: 'Collins Street',
    street: '101 Collins Street',
    suburb: 'Melbourne',
    state: 'VIC',
    postcode: '3000',
  },
  {
    formatted: '200 Adelaide Street, Brisbane QLD 4000',
    unit_number: '',
    street_number: '200',
    street_name: 'Adelaide Street',
    street: '200 Adelaide Street',
    suburb: 'Brisbane',
    state: 'QLD',
    postcode: '4000',
  },
  {
    formatted: '75 Murray Street, Perth WA 6000',
    unit_number: '',
    street_number: '75',
    street_name: 'Murray Street',
    street: '75 Murray Street',
    suburb: 'Perth',
    state: 'WA',
    postcode: '6000',
  },
];

// Shown when Google refuses the request (key not authorised for this domain,
// Places API not enabled on the project, quota exhausted). The customer can
// always type the address in by hand, so say so rather than leaving a dead box.
const SERVICE_ERROR_MESSAGE =
  'Address search is unavailable right now. Type the address in yourself and complete the fields below.';

const componentValue = (components, type, useShort) => {
  if (!components) return '';
  const found = components.find((c) => Array.isArray(c.types) && c.types.includes(type));
  if (!found) return '';
  return useShort ? found.short_name : found.long_name;
};

// Build the structured shape every parent component expects from an
// `onSelect` event. We return BOTH split pieces (unit/number/name) and a
// combined `street` for backwards-compatible consumers.
const placeToSelection = (place) => {
  const comps = place && place.address_components ? place.address_components : [];
  const streetNumber = componentValue(comps, 'street_number');
  const route = componentValue(comps, 'route');
  const subpremise = componentValue(comps, 'subpremise');
  const locality =
    componentValue(comps, 'locality') ||
    componentValue(comps, 'postal_town') ||
    componentValue(comps, 'sublocality') ||
    componentValue(comps, 'sublocality_level_1');
  const state = componentValue(comps, 'administrative_area_level_1', true);
  const postcode = componentValue(comps, 'postal_code');
  const countryCode = componentValue(comps, 'country', true);
  const countryName = componentValue(comps, 'country');

  const combinedStreet = [
    subpremise ? `Unit ${subpremise}` : null,
    [streetNumber, route].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  const formatted =
    (place && place.formatted_address) ||
    [combinedStreet, locality, state, postcode].filter(Boolean).join(', ');

  return {
    unit_number: subpremise || '',
    street_number: streetNumber || '',
    street_name: route || '',
    street: combinedStreet || (place && place.formatted_address) || '',
    suburb: locality || '',
    state: state || '',
    postcode: postcode || '',
    country: (countryCode || '').toLowerCase(),
    country_name: countryName || '',
    formatted,
  };
};

class AddressAutocomplete extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      query: props.value || '',
      open: false,
      highlight: -1,
      // Google Places "predictions" when wired up. Stub fallback otherwise.
      predictions: [],
      loading: false,
      // Set when Google rejects us (key not authorised for this domain, Places
      // API not enabled, quota exhausted). Without this the box just sits
      // there returning nothing and looks broken for no visible reason.
      serviceError: '',
    };
    this.containerRef = React.createRef();
    this.debounceTimer = null;
    this.googleReady = false;
    this.autocompleteService = null;
    this.placesService = null;
    this.sessionToken = null;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick);
    this.initGoogle();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && this.props.value !== this.state.query) {
      this.setState({ query: this.props.value || '' });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.timeoutTimer) clearTimeout(this.timeoutTimer);
  }

  initGoogle = async () => {
    if (!isGoogleMapsConfigured()) return;
    try {
      const google = await loadGoogleMaps();
      if (!google) return;
      // Google calls this global when the key itself is rejected (bad key,
      // domain not on the key's referrer allowlist, billing disabled). It is
      // the only signal for those failures, so surface it instead of leaving
      // the box silently empty.
      if (!window.gm_authFailure) {
        window.gm_authFailure = () => {
          // eslint-disable-next-line no-console
          console.error('[AddressAutocomplete] Google Maps rejected the API key for this domain.');
          window.__gmAuthFailed = true;
        };
      }
      this.autocompleteService = new google.maps.places.AutocompleteService();
      // PlacesService requires a DOM node to attach to. A throw-away div is fine.
      const attribution = document.createElement('div');
      this.placesService = new google.maps.places.PlacesService(attribution);
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
      this.googleReady = true;
      // If the user already typed something while we were loading, fire it now.
      if ((this.state.query || '').length >= 3 && this.state.open) {
        this.fetchPredictions(this.state.query);
      }
    } catch (e) {
      // Swallow - fallback list will be used below.
      // eslint-disable-next-line no-console
      console.warn('[AddressAutocomplete] Google Maps failed to load:', e);
    }
  };

  handleOutsideClick = (e) => {
    if (this.containerRef.current && !this.containerRef.current.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  // Stub mode predictions (no Google key) - filter the static list.
  stubFiltered = (q) => {
    const lower = (q || '').toLowerCase().trim();
    if (!lower) return STUB_SUGGESTIONS;
    return STUB_SUGGESTIONS.filter((s) => s.formatted.toLowerCase().includes(lower));
  };

  fetchPredictions = (q) => {
    if (!this.googleReady) {
      // Stub mode: synchronous filter.
      this.setState({ predictions: this.stubFiltered(q), loading: false });
      return;
    }
    if (!q || q.length < 3) {
      this.setState({ predictions: [], loading: false });
      return;
    }
    if (window.__gmAuthFailed) {
      this.setState({ predictions: [], loading: false, serviceError: SERVICE_ERROR_MESSAGE });
      return;
    }
    this.setState({ loading: true });
    // A rejected key makes Google drop the request on the floor: the callback
    // below never fires at all. Fall back to the same message so the box does
    // not sit on a spinner forever.
    if (this.timeoutTimer) clearTimeout(this.timeoutTimer);
    this.timeoutTimer = setTimeout(() => {
      if (this.state.loading) {
        // eslint-disable-next-line no-console
        console.warn('[AddressAutocomplete] Places request timed out with no response.');
        this.setState({ predictions: [], loading: false, serviceError: SERVICE_ERROR_MESSAGE });
      }
    }, 5000);
    // Default to AU when no country is specified so behaviour is unchanged
    // for the rest of the app. The address forms now pass a real country.
    // `unrestricted` searches worldwide (used where there is no country field,
    // e.g. employer addresses).
    const country = (this.props.country || 'au').toLowerCase();
    const request = {
      input: q,
      types: ['address'],
      sessionToken: this.sessionToken,
    };
    if (!this.props.unrestricted) {
      request.componentRestrictions = { country };
    }
    this.autocompleteService.getPlacePredictions(request, (results, status) => {
      if (this.timeoutTimer) clearTimeout(this.timeoutTimer);
      // PlacesServiceStatus.OK === 'OK'. ZERO_RESULTS is a normal empty
      // answer; anything else means the request was refused and the customer
      // needs to be told rather than shown an empty box.
      if (status === 'OK' && results) {
        this.setState({ predictions: results, loading: false, serviceError: '' });
        return;
      }
      if (status === 'ZERO_RESULTS') {
        this.setState({ predictions: [], loading: false, serviceError: '' });
        return;
      }
      // eslint-disable-next-line no-console
      console.warn('[AddressAutocomplete] Places request failed:', status);
      this.setState({
        predictions: [],
        loading: false,
        serviceError: SERVICE_ERROR_MESSAGE,
      });
    });
  };

  handleChange = (e) => {
    const next = e.target.value;
    this.setState({ query: next, open: true, highlight: -1 });
    if (this.props.onChange) this.props.onChange(next);

    // Debounce the actual prediction call.
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.fetchPredictions(next), 200);
  };

  pickPrediction = (prediction) => {
    if (!this.googleReady) {
      // Stub mode - prediction is already a Selection-shaped object.
      this.applySelection(prediction);
      return;
    }
    // Google mode - resolve the placeId to a Selection via getDetails.
    this.placesService.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address'],
        sessionToken: this.sessionToken,
      },
      (place, status) => {
        if (status !== 'OK' || !place) return;
        this.applySelection(placeToSelection(place));
        // Per Google's billing model, mint a new session token after we
        // resolve a place.
        if (window.google && window.google.maps && window.google.maps.places) {
          this.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
        }
      }
    );
  };

  applySelection = (selection) => {
    this.setState({ query: selection.formatted, open: false, highlight: -1 });
    if (this.props.onChange) this.props.onChange(selection.formatted);
    if (this.props.onSelect) this.props.onSelect(selection);
  };

  handleKeyDown = (e) => {
    const list = this.state.predictions;
    if (!this.state.open) {
      if (e.key === 'ArrowDown') this.setState({ open: true });
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setState({ highlight: Math.min(this.state.highlight + 1, list.length - 1) });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setState({ highlight: Math.max(this.state.highlight - 1, 0) });
    } else if (e.key === 'Enter' && this.state.highlight >= 0) {
      e.preventDefault();
      this.pickPrediction(list[this.state.highlight]);
    } else if (e.key === 'Escape') {
      this.setState({ open: false });
    }
  };

  handleFocus = () => {
    this.setState({ open: true });
    if (this.googleReady && (this.state.query || '').length >= 3) {
      this.fetchPredictions(this.state.query);
    } else if (!this.googleReady) {
      this.setState({ predictions: this.stubFiltered(this.state.query) });
    }
  };

  renderPredictionLabel = (prediction) => {
    if (this.googleReady && prediction.structured_formatting) {
      const main = prediction.structured_formatting.main_text || '';
      const secondary = prediction.structured_formatting.secondary_text || '';
      return (
        <>
          <span className="font-medium">{main}</span>
          {secondary ? (
            <span className="ml-1 text-[#6B7280]">{secondary}</span>
          ) : null}
        </>
      );
    }
    return <span>{prediction.formatted || prediction.description}</span>;
  };

  render() {
    const { label, required, placeholder, hint, fieldId, error } = this.props;
    const list = this.state.predictions;
    const hasError = !!error;
    const usingGoogle = this.googleReady;

    return (
      <div className="w-full" ref={this.containerRef} data-field-id={fieldId || undefined}>
        {label ? (
          <label className="block text-sm font-medium text-[#1F2937] mb-2">
            {label}
            {required ? <span className="text-[#DC2626] ml-1">*</span> : null}
          </label>
        ) : null}
        <div className="relative">
          <div
            className="flex items-center w-full rounded-lg border bg-white focus-within:ring-2"
            style={{
              borderColor: hasError ? '#DC2626' : '#D1D5DB',
              boxShadow: hasError ? '0 0 0 1px #DC2626' : undefined,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 ml-3 text-[#6B7280] flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.3-4.3M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <input
              type="text"
              required={required}
              placeholder={placeholder || 'Start typing an address...'}
              value={this.state.query}
              onChange={this.handleChange}
              onFocus={this.handleFocus}
              onKeyDown={this.handleKeyDown}
              aria-invalid={hasError || undefined}
              autoComplete="off"
              className="w-full bg-transparent outline-none py-2.5 px-3 text-sm sm:text-base text-[#1F2937]"
            />
            {this.state.loading ? (
              <span className="pr-3 text-xs text-[#6B7280]">...</span>
            ) : null}
          </div>

          {this.state.open && list.length > 0 ? (
            <div
              className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-auto rounded-lg border bg-white shadow-lg"
              style={{ borderColor: '#D1D5DB' }}
            >
              {!usingGoogle ? (
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB] bg-[#F5F6FA]">
                  Stub results - configure REACT_APP_GOOGLE_MAPS_API_KEY for live results
                </div>
              ) : null}
              {list.map((s, i) => (
                <button
                  type="button"
                  key={usingGoogle ? s.place_id : s.formatted}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    this.pickPrediction(s);
                  }}
                  onMouseEnter={() => this.setState({ highlight: i })}
                  className="block w-full text-left px-3 py-2.5 text-sm text-[#1F2937] hover:bg-[#EFEAFE]"
                  style={{
                    backgroundColor: this.state.highlight === i ? '#EFEAFE' : 'transparent',
                  }}
                >
                  {this.renderPredictionLabel(s)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {hasError ? (
          <p className="mt-1 text-xs font-medium text-[#DC2626]">{error}</p>
        ) : this.state.serviceError ? (
          <p className="mt-1 text-xs font-medium" style={{ color: '#92400E' }}>
            {this.state.serviceError}
          </p>
        ) : hint ? (
          <p className="mt-1 text-xs text-[#6B7280]">{hint}</p>
        ) : null}
      </div>
    );
  }
}

export default AddressAutocomplete;
